import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { fetchConditionalTagResultDetail } from "../../services/taggingResultsService";
import { AI_HUB_PATH } from "./constants";
import AiHubDetailBreadcrumb from "./AiHubDetailBreadcrumb";
import {
  AiHubDetailErrorState,
  AiHubDetailLoadingState,
} from "./AiHubDetailPageState";
import AiHubTaggingDetailHeader from "./AiHubTaggingDetailHeader";
import {
  conditionalStatusPresentation,
  displayOrDash,
  formatMatchedCondition,
  isTaggingResultDetailRequestReady,
  MISSING_CONDITIONAL_DETAIL_QUERY_MESSAGE,
  useTaggingResultDetailRequest,
  type ConditionalTagResultDetail,
} from "./taggingResultDetailData";

const cellSx = {
  borderColor: "#eef0f3",
  px: 2,
  py: 2.25,
  fontSize: 13,
  color: "#374151",
  verticalAlign: "middle",
} as const;

export default function ConditionalTaggingResultDetailPage() {
  const navigate = useNavigate();
  const request = useTaggingResultDetailRequest("conditionalTaggingResult");
  const { objectId, objectSubType, objectType, version } = request;
  const [detail, setDetail] = useState<ConditionalTagResultDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const returnToList = () => {
    navigate(AI_HUB_PATH, { state: { tab: "conditionalTaggingResult" } });
  };

  useEffect(() => {
    let cancelled = false;
    const nextRequest = { objectId, objectSubType, objectType, version };

    if (!isTaggingResultDetailRequestReady(nextRequest)) {
      setDetail(null);
      setError(MISSING_CONDITIONAL_DETAIL_QUERY_MESSAGE);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    fetchConditionalTagResultDetail(nextRequest)
      .then((next) => {
        if (!cancelled) {
          setDetail(next);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setDetail(null);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load Conditional Classification Result.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [objectId, objectSubType, objectType, version]);

  if (loading && !detail) {
    return <AiHubDetailLoadingState />;
  }

  if (error && !detail) {
    return (
      <AiHubDetailErrorState
        resultId={objectId}
        message={error}
        onBack={returnToList}
      />
    );
  }

  if (!detail) return null;

  const status = conditionalStatusPresentation(detail.status);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f7f8fa",
        p: { xs: 2, md: 3 },
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ mb: 2 }}>
        <AiHubDetailBreadcrumb
          currentLabel={objectId}
          onBack={returnToList}
        />
      </Box>

      <Box sx={{ bgcolor: "#fff", borderRadius: "8px", p: { xs: 2, md: 3 } }}>
        <AiHubTaggingDetailHeader
          title={detail.title || objectId}
          statusLabel={status.label}
          statusTone={status.tone}
          references={detail.references}
        />

        <Box sx={{ mt: 3, pt: 2.5, borderTop: "1px solid #e5e7eb" }}>
          <Typography sx={{ mb: 1.5, fontSize: 16, fontWeight: 600 }}>
            Classification List
          </Typography>
          <TableContainer>
            <Table aria-label="Classification List">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8f9fb" }}>
                  <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                    Dimension
                  </TableCell>
                  <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                    Tag Value
                  </TableCell>
                  <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                    Rule ID
                  </TableCell>
                  <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                    Matching Condition
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detail.rules.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell sx={cellSx}>
                      {displayOrDash(row.concept)}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      {displayOrDash(row.tagValue)}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      {displayOrDash(row.ruleId)}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, minWidth: 280 }}>
                      {displayOrDash(formatMatchedCondition(row.matchedCondition))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Box>
  );
}
